import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { RadioButton } from "primereact/radiobutton";
import { Checkbox } from "primereact/checkbox";
import { Rating } from "primereact/rating";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { useToast } from "../../utils/toast";
import { useAuth } from "../../utils/auth";
import dynamicSurveyService, { DynamicAnswerSubmitDto, DynamicSectionDto, DynamicQuestionDto, DynamicRuleDto } from "../../services/forms/dynamic.service";

function getAnswerValue(question: DynamicQuestionDto, answer?: DynamicAnswerSubmitDto): any {
  if (!answer) return undefined;
  if (["single_choice", "dropdown", "multiple_choice"].includes(question.question_type)) {
    const selectedIds = answer.selected_choices ?? [];
    return (question.choices ?? []).filter((choice) => choice.id != null && selectedIds.includes(choice.id)).map((choice) => choice.key);
  }
  if (question.question_type === "rating") return answer.rating_answer;
  if (question.question_type === "number") return answer.number_answer;
  return answer.text_answer;
}

function conditionMatches(condition: DynamicRuleDto["conditions"][number], questions: DynamicQuestionDto[], answers: Record<number, DynamicAnswerSubmitDto>, reachableKeys: Set<string>): boolean {
  const question = questions.find((item) => item.key === condition.question_key);
  if (!question || !question.id || !reachableKeys.has(condition.question_key)) return false;
  const value = getAnswerValue(question, answers[question.id]);
  const answered = Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && String(value).trim() !== "";
  if (condition.operator === "answered") return answered;
  if (condition.operator === "not_answered") return !answered;
  if (condition.operator === "equals") return Array.isArray(value) ? value.length === 1 && value[0] === condition.value : value === condition.value || String(value ?? "") === String(condition.value ?? "");
  if (condition.operator === "not_equals") return answered && !conditionMatches({ ...condition, operator: "equals" }, questions, answers, reachableKeys);
  if (condition.operator === "contains") return Array.isArray(value) && value.includes(condition.value as string);
  if (condition.operator === "contains_all") return Array.isArray(value) && (Array.isArray(condition.value) ? condition.value : [condition.value]).every((item) => value.includes(item as string));
  if (condition.operator === "contains_any") return Array.isArray(value) && (Array.isArray(condition.value) ? condition.value : [condition.value]).some((item) => value.includes(item as string));
  const left = Number(value);
  const right = Number(condition.value);
  if (!Number.isFinite(left) || !Number.isFinite(right)) return false;
  if (condition.operator === "greater_than") return left > right;
  if (condition.operator === "greater_or_equal") return left >= right;
  if (condition.operator === "less_than") return left < right;
  if (condition.operator === "less_or_equal") return left <= right;
  return false;
}

function ruleMatches(rule: DynamicRuleDto, questions: DynamicQuestionDto[], answers: Record<number, DynamicAnswerSubmitDto>, reachableKeys: Set<string>) {
  if (rule.conditions.length === 0) return true;
  const results = rule.conditions.map((condition) => conditionMatches(condition, questions, answers, reachableKeys));
  return rule.match_all ? results.every(Boolean) : results.some(Boolean);
}

function hiddenKeysFor(rules: DynamicRuleDto[], questions: DynamicQuestionDto[], answers: Record<number, DynamicAnswerSubmitDto>, reachableKeys: Set<string>) {
  return new Set(rules.filter((rule) => rule.action_type === "hide_question" && ruleMatches(rule, questions, answers, reachableKeys)).map((rule) => rule.target_question_key).filter((key): key is string => !!key));
}

function resolveSectionPath(sections: DynamicSectionDto[], questions: DynamicQuestionDto[], answers: Record<number, DynamicAnswerSubmitDto>, rules: DynamicRuleDto[]) {
  const ordered = [...sections].sort((a, b) => a.order - b.order);
  const indexByKey = new Map(ordered.map((section, index) => [section.key, index]));
  const path: string[] = [];
  const reachableKeys = new Set<string>();
  const appliedRouteRuleIndexes = new Set<number>();
  const sortedRules = [...rules].sort((a, b) => a.priority - b.priority || (a.id ?? 0) - (b.id ?? 0));
  let current: string | undefined = ordered[0]?.key;
  while (current && !path.includes(current)) {
    const sectionKey: string = current;
    path.push(sectionKey);
    const sectionQuestions = questions.filter((question) => question.section_key === sectionKey).sort((a, b) => a.order - b.order);
    sectionQuestions.forEach((question) => question.key && reachableKeys.add(question.key));
    let next: string | undefined = ordered[(indexByKey.get(sectionKey) ?? -1) + 1]?.key;
    const routeCandidates = sortedRules
      .map((rule, index) => ({ rule, index }))
      .filter(({ rule, index }) =>
      !appliedRouteRuleIndexes.has(index) &&
      (!rule.source_section_key || rule.source_section_key === sectionKey) &&
      ["goto_section", "submit"].includes(rule.action_type) &&
      ruleMatches(rule, questions, answers, reachableKeys)
      )
      .sort((a, b) => Number(!a.rule.conditions.length) - Number(!b.rule.conditions.length) || a.rule.priority - b.rule.priority || (a.rule.id ?? 0) - (b.rule.id ?? 0));
    const selectedRoute = routeCandidates[0];
    const route = selectedRoute?.rule;
    if (selectedRoute) appliedRouteRuleIndexes.add(selectedRoute.index);
    if (route?.action_type === "submit") next = undefined;
    else if (route?.action_type === "goto_section" && route.target_section_key) next = route.target_section_key;
    current = next;
  }
  return path;
}

const DynamikAnketDoldur = () => {
  const { id, token } = useParams<{ id?: string; token?: string }>();
  const surveyIdentifier = id;
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useAuth();

  const [answers, setAnswers] = useState<Record<number, DynamicAnswerSubmitDto>>({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentSectionKey, setCurrentSectionKey] = useState("");
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["dynamicSurveyDetail", surveyIdentifier, token],
    queryFn: () => token ? dynamicSurveyService.getPublicSurvey(token) : (surveyIdentifier ? dynamicSurveyService.getSurveyDetail(surveyIdentifier) : Promise.reject("Anket ID eksik")),
    enabled: !!token || !!surveyIdentifier,
  });

  const survey = data?.result?.data;
  const questions = survey?.questions ?? [];
  const sections = survey?.sections ?? [];
  const rules = survey?.rules ?? [];
  const sectionPath = resolveSectionPath(sections, questions, answers, rules);
  const sectionPathKey = sectionPath.join("|");
  const activePathIndex = Math.max(0, sectionPath.indexOf(currentSectionKey));
  const activeQuestionKeys = new Set(questions.filter((question) => sectionPath.slice(0, activePathIndex + 1).includes(question.section_key ?? "")).map((question) => question.key).filter((key): key is string => !!key));
  const hiddenQuestionKeys = hiddenKeysFor(rules, questions, answers, activeQuestionKeys);

  useEffect(() => {
    if (!sectionPath.length) return;
    if (!currentSectionKey || !sectionPath.includes(currentSectionKey)) setCurrentSectionKey(sectionPath[0]);
    const reachableSectionKeys = new Set(sectionPath);
    const reachableQuestionIds = new Set(questions.filter((question) => reachableSectionKeys.has(question.section_key ?? "") && !hiddenQuestionKeys.has(question.key ?? "")).map((question) => question.id ?? -1));
    setAnswers((previous) => {
      const next = Object.fromEntries(Object.entries(previous).filter(([questionId]) => reachableQuestionIds.has(Number(questionId))));
      return Object.keys(next).length === Object.keys(previous).length ? previous : next;
    });
    // The route signature changes only when a branching answer changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionPathKey, survey?.id, Array.from(hiddenQuestionKeys).sort().join("|")]);

  if (isLoading) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
        <ProgressSpinner />
      </div>
    );
  }

  if (!survey) {
    const loadError = (data?.result as any)?.error;
    const needsLogin = typeof loadError === "string" && loadError.includes("giriş yapmalısınız");
    const isLimitReached = typeof loadError === "string" && (loadError.includes("sınır") || loadError.includes("kontenjan"));
    const isExpired = typeof loadError === "string" && (loadError.includes("süresi") || loadError.includes("sona erdi"));
    const isNotStarted = typeof loadError === "string" && loadError.includes("başlamadı");

    let title = "Anket Bulunamadı";
    let desc = typeof loadError === "string" ? loadError : "Anket bulunamadı veya erişim izniniz yok.";
    let icon = "pi pi-exclamation-triangle text-yellow-500";

    if (isLimitReached) {
      title = "Katılım Kontenjanı Dolmuştur";
      desc = "Bu anket için belirlenen yanıt üst sınırına (kontenjana) ulaşılmıştır. İlginiz için teşekkür ederiz; artık yeni yanıt kabul edilmemektedir.";
      icon = "pi pi-lock text-orange-500";
    } else if (isExpired) {
      title = "Anket Süresi Sona Erdi";
      desc = "Bu anketin yanıtlanma süresi dolmuştur. Katılımınız ve ilginiz için teşekkür ederiz.";
      icon = "pi pi-calendar-times text-500";
    } else if (isNotStarted) {
      title = "Anket Henüz Başlamadı";
      desc = "Bu anket henüz yanıtlara açılmamıştır. Başlangıç zamanından sonra tekrar deneyebilirsiniz.";
      icon = "pi pi-clock text-blue-500";
    }

    return (
      <div className="flex flex-column align-items-center justify-content-center p-6">
        <div
          className="flex flex-column align-items-center justify-content-center border-round-xl p-5 shadow-1 text-center"
          style={{ background: "var(--surface-card)", border: "1px solid var(--surface-border)", maxWidth: "520px", width: "100%" }}
        >
          <i className={icon} style={{ fontSize: "3rem" }} />
          <h3 className="text-900 font-bold mt-3 mb-2 text-xl">{title}</h3>
          <p className="text-600 line-height-3 m-0 mb-4">{desc}</p>
          {needsLogin ? (
            <Button label="Giriş Yap" icon="pi pi-sign-in" onClick={() => navigate("/loginsebis")} />
          ) : (
            <Button label="Geri Dön" icon="pi pi-arrow-left" outlined onClick={() => navigate(token ? "/" : "/app/anketler")} />
          )}
        </div>
      </div>
    );
  }

  if (survey.is_closed && !survey.has_submitted && !submitted) {
    let title = "Anket Yanıtlara Kapalı";
    let desc = "Bu anket şu anda yanıt kabul etmemektedir.";
    let icon = "pi pi-lock text-orange-500";

    if (survey.closure_reason === "limit_reached") {
      title = "Katılım Kontenjanı Dolmuştur";
      desc = `Bu anket için belirlenen maksimum yanıt sınırına (${survey.response_limit ?? ""} katılımcı) ulaşılmıştır. Gösterdiğiniz ilgi için teşekkür ederiz; yeni yanıt kabul edilmemektedir.`;
      icon = "pi pi-lock text-orange-500";
    } else if (survey.closure_reason === "expired") {
      title = "Anket Süresi Sona Erdi";
      desc = "Bu anketin yanıtlanma süresi dolmuştur. Katılımınız ve ilginiz için teşekkür ederiz.";
      icon = "pi pi-calendar-times text-500";
    } else if (survey.closure_reason === "not_started") {
      title = "Anket Henüz Başlamadı";
      desc = `Bu anket henüz yanıtlara açılmamıştır.${survey.starts_at ? ` Başlangıç tarihi: ${new Date(survey.starts_at).toLocaleString("tr-TR")}` : ""}`;
      icon = "pi pi-clock text-blue-500";
    }

    return (
      <div className="flex flex-column align-items-center justify-content-center p-6">
        <div
          className="flex flex-column align-items-center justify-content-center border-round-xl p-5 shadow-1 text-center"
          style={{ background: "var(--surface-card)", border: "1px solid var(--surface-border)", maxWidth: "520px", width: "100%" }}
        >
          <i className={icon} style={{ fontSize: "3rem" }} />
          <h3 className="text-900 font-bold mt-3 mb-2 text-xl">{title}</h3>
          <p className="text-600 line-height-3 m-0 mb-4">{desc}</p>
          <Button label="Anket Listesine Dön" icon="pi pi-arrow-left" outlined onClick={() => navigate(token ? "/" : "/app/anketler")} />
        </div>
      </div>
    );
  }

  if (survey.has_submitted || submitted) {
    return (
      <div className="flex flex-column align-items-center justify-content-center p-6">
        <div
          className="flex flex-column align-items-center justify-content-center border-round-xl p-5"
          style={{ background: "var(--green-50)", border: "2px solid var(--green-300)", maxWidth: "480px", width: "100%" }}
        >
          <i className="pi pi-check-circle text-green-500" style={{ fontSize: "3rem" }} />
          <h3 className="text-900 font-bold mt-3 mb-1 text-xl">Teşekkürler!</h3>
          <p className="text-600 text-center m-0">Bu ankete daha önce katıldınız. Cevaplarınız başarıyla kaydedildi.</p>
          <Button
            label="Anket Listesine Dön"
            icon="pi pi-arrow-left"
            className="mt-4"
            onClick={() => navigate("/app/anketler")}
          />
        </div>
      </div>
    );
  }

  const currentSection = sections.find((section) => section.key === currentSectionKey) ?? sections[0];
  const visibleQuestions = questions.filter((question) => question.section_key === currentSection?.key && !hiddenQuestionKeys.has(question.key ?? "")).sort((a, b) => a.order - b.order);
  const reachableQuestionKeys = new Set(questions.filter((question) => sectionPath.includes(question.section_key ?? "")).map((question) => question.key).filter((key): key is string => !!key));
  const allHiddenQuestionKeys = hiddenKeysFor(rules, questions, answers, reachableQuestionKeys);
  const reachableQuestions = questions.filter((question) => sectionPath.includes(question.section_key ?? "") && !allHiddenQuestionKeys.has(question.key ?? ""));
  const currentPathIndex = sectionPath.indexOf(currentSection?.key ?? "");
  const answeredCount = reachableQuestions.filter((question) => {
    const answer = answers[question.id ?? -1];
    return !!answer && (!!answer.text_answer?.trim() || !!answer.selected_choices?.length || answer.rating_answer != null || answer.number_answer != null);
  }).length;

  const updateTextAnswer = (questionId: number, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { question_id: questionId, text_answer: value },
    }));
  };

  const updateSingleChoice = (questionId: number, choiceId: number | null) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { question_id: questionId, selected_choices: choiceId == null ? [] : [choiceId] },
    }));
  };

  const updateMultiChoice = (questionId: number, choiceId: number, checked: boolean) => {
    setAnswers((prev) => {
      const current = prev[questionId]?.selected_choices ?? [];
      const updated = checked ? [...current, choiceId] : current.filter((id) => id !== choiceId);
      return { ...prev, [questionId]: { question_id: questionId, selected_choices: updated } };
    });
  };

  const updateRating = (questionId: number, value: number) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { question_id: questionId, rating_answer: value },
    }));
  };

  const updateNumberAnswer = (questionId: number, value: number | null) => {
    setAnswers((prev) => ({ ...prev, [questionId]: { question_id: questionId, number_answer: value } }));
  };

  const handleSubmit = async () => {
    if (!token && !auth.getUser().access_token) {
      toast.show("Anket doldurmak için giriş yapmalısınız.", "error");
      navigate("/loginsebis");
      return;
    }

    setSubmitting(true);
    try {
      const reachableQuestionIds = new Set(reachableQuestions.map((question) => question.id));
      const answersArray = Object.values(answers).filter((answer) => reachableQuestionIds.has(answer.question_id));
      const res = token
        ? await dynamicSurveyService.submitPublicSurveyResponse(token, { answers: answersArray })
        : await dynamicSurveyService.submitSurveyResponse(survey?.id ?? surveyIdentifier!, { answers: answersArray });

      if (res.result && !res.result.error) {
        setSubmitted(true);
        setShowConfirm(false);
        toast.show("Cevaplarınız başarıyla kaydedildi.", "success");
      } else {
        let errMsg = "Bir hata oluştu.";
        if (res.result?.error) {
          if (typeof res.result.error === "string") {
            errMsg = res.result.error;
          } else if (typeof res.result.error === "object") {
            errMsg = Object.values(res.result.error).flat().join(" ") || "Bir hata oluştu.";
          }
        }
        toast.show(errMsg, "error");
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? "Bir hata oluştu. Lütfen tekrar deneyin.";
      toast.show(typeof msg === "string" ? msg : "Bir hata oluştu. Lütfen tekrar deneyin.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const isQuestionAnswered = (question: DynamicQuestionDto) => {
    const answer = answers[question.id ?? -1];
    if (!answer) return false;
    if (question.question_type === "text") return !!answer.text_answer?.trim();
    if (question.question_type === "date") return !!answer.text_answer?.trim();
    if (question.question_type === "number") return answer.number_answer != null;
    if (question.question_type === "rating") return answer.rating_answer != null && answer.rating_answer > 0;
    return (answer.selected_choices?.length ?? 0) > 0;
  };

  const validateCurrentSection = () => {
    const invalidNumber = visibleQuestions.find((question) => {
      if (question.question_type !== "number") return false;
      const value = answers[question.id ?? -1]?.number_answer;
      if (value == null) return false;
      return (question.config?.min != null && value < question.config.min) || (question.config?.max != null && value > question.config.max);
    });
    if (invalidNumber) {
      setShowValidationErrors(true);
      toast.show(`“${invalidNumber.text}” için sayı izin verilen sınırların dışında.`, "warn");
      return false;
    }
    const missing = visibleQuestions.filter((question) => {
      if (!question.is_required) return false;
      return !isQuestionAnswered(question);
    });

    if (missing.length > 0) {
      setShowValidationErrors(true);
      toast.show("Eksik sorular var. Lütfen zorunlu soruları doldurunuz.", "warn");
      const firstMissingId = missing[0]?.id;
      if (firstMissingId != null) {
        setTimeout(() => {
          const el = document.getElementById(`question-card-${firstMissingId}`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 50);
      }
      return false;
    }
    return true;
  };

  const handlePrevSection = () => {
    setShowValidationErrors(false);
    setCurrentSectionKey(sectionPath[currentPathIndex - 1]);
  };

  const handleNextSection = () => {
    if (validateCurrentSection()) {
      setShowValidationErrors(false);
      setCurrentSectionKey(sectionPath[currentPathIndex + 1]);
    }
  };

  return (
    <div className="p-3 md:p-4" style={{ maxWidth: "800px", margin: "0 auto" }}>
      <div className="mb-4">
        <Button
          icon="pi pi-arrow-left"
          label="Geri"
          text
          severity="secondary"
          className="mb-2 p-0"
          onClick={() => navigate("/app/anketler")}
        />
        <h2 className="text-900 font-bold text-2xl m-0">{survey.title}</h2>
        {survey.description && <p className="text-500 mt-1 mb-0">{survey.description}</p>}
      </div>

      <div className="mb-4">
        {currentSection?.title && (
          <span className="text-600 font-medium">{currentSection.title}</span>
        )}
        {currentSection?.description && (
          <p className="text-500 text-sm mt-2 mb-0">{currentSection.description}</p>
        )}
      </div>

      <div className="flex flex-column gap-3">
        {visibleQuestions.map((question, index) => {
          const currentAnswer = answers[question.id!];
          const isAnswered = isQuestionAnswered(question);
          const isMissing = showValidationErrors && question.is_required && !isAnswered;

          return (
            <div
              key={question.id}
              id={`question-card-${question.id}`}
              className="w-full transition-all"
            >
              <Card
                className={`border-round-xl shadow-1 transition-all ${
                  isMissing ? "border-1 border-red-500 surface-50" : ""
                }`}
                style={{
                  borderLeft: `4px solid ${
                    isMissing
                      ? "var(--red-500)"
                      : isAnswered
                      ? "var(--green-400)"
                      : "var(--surface-300)"
                  }`,
                  boxShadow: isMissing ? "0 0 0 1px var(--red-400)" : undefined,
                }}
              >
                <div className="flex align-items-start gap-2 mb-3">
                  <span
                    className="flex align-items-center justify-content-center border-circle font-bold text-sm flex-shrink-0"
                    style={{
                      width: "28px",
                      height: "28px",
                      background: isMissing
                        ? "var(--red-100)"
                        : isAnswered
                        ? "var(--green-100)"
                        : "var(--surface-200)",
                      color: isMissing
                        ? "var(--red-700)"
                        : isAnswered
                        ? "var(--green-700)"
                        : "var(--text-color-secondary)",
                    }}
                  >
                    {isMissing ? (
                      <i className="pi pi-exclamation-triangle text-xs" />
                    ) : isAnswered ? (
                      <i className="pi pi-check text-xs" />
                    ) : (
                      index + 1
                    )}
                  </span>
                  <div className="flex-1">
                    <p className={`font-medium m-0 ${isMissing ? "text-red-900" : "text-900"}`} style={{ lineHeight: "1.5" }}>
                      {question.text}{question.is_required && <span className="text-red-500 ml-1 font-bold">*</span>}
                    </p>
                    {question.help_text && <p className="text-500 text-sm mt-1 mb-0">{question.help_text}</p>}
                    <div className="flex align-items-center flex-wrap gap-2 mt-1">
                      <Tag
                        value={
                          question.question_type === "text"
                            ? "Serbest Metin"
                            : question.question_type === "date"
                            ? "Tarih"
                            : question.question_type === "number"
                            ? "Sayı"
                            : question.question_type === "single_choice"
                            ? "Tek Seçmeli"
                            : question.question_type === "dropdown"
                            ? "Açılır Liste"
                            : question.question_type === "multiple_choice"
                            ? "Çok Seçmeli"
                            : "Derecelendirme"
                        }
                        severity="info"
                        style={{ fontSize: "0.7rem" }}
                      />
                      {isMissing && (
                        <span className="text-red-600 text-xs font-semibold flex align-items-center gap-1">
                          <i className="pi pi-exclamation-circle text-xs" /> Bu sorunun cevaplanması zorunludur
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {question.question_type === "text" && (
                  <InputTextarea
                    value={currentAnswer?.text_answer ?? ""}
                    onChange={(e) => updateTextAnswer(question.id!, e.target.value)}
                    rows={3}
                    placeholder="Cevabınızı buraya yazın..."
                    className={`w-full ${isMissing ? "p-invalid" : ""}`}
                    autoResize
                  />
                )}

                {question.question_type === "date" && (
                  <InputText
                    type="date"
                    value={currentAnswer?.text_answer ?? ""}
                    onChange={(e) => updateTextAnswer(question.id!, e.target.value)}
                    className={`w-full md:w-20rem ${isMissing ? "p-invalid" : ""}`}
                  />
                )}

                {question.question_type === "number" && (
                  <InputNumber
                    value={currentAnswer?.number_answer ?? null}
                    onValueChange={(e) => updateNumberAnswer(question.id!, e.value ?? null)}
                    min={question.config?.min ?? undefined}
                    max={question.config?.max ?? undefined}
                    className={`w-full md:w-20rem ${isMissing ? "p-invalid" : ""}`}
                    inputClassName="w-full"
                  />
                )}

                {question.question_type === "single_choice" && (
                  <div className={`flex flex-column gap-2 p-2 border-round ${isMissing ? "border-1 border-red-300 surface-50" : ""}`}>
                    {question.choices?.map((choice) => (
                      <div key={choice.id} className="flex align-items-center gap-2">
                        <RadioButton
                          inputId={`q${question.id}_c${choice.id}`}
                          value={choice.id}
                          checked={currentAnswer?.selected_choices?.[0] === choice.id}
                          onChange={() => updateSingleChoice(question.id!, choice.id!)}
                        />
                        <label htmlFor={`q${question.id}_c${choice.id}`} className="cursor-pointer text-900">
                          {choice.text}
                        </label>
                      </div>
                    ))}
                  </div>
                )}

                {question.question_type === "dropdown" && (
                  <Dropdown
                    value={currentAnswer?.selected_choices?.[0] ?? null}
                    options={question.choices}
                    optionLabel="text"
                    optionValue="id"
                    placeholder="Bir seçenek belirleyin"
                    showClear={!question.is_required}
                    onChange={(e) => updateSingleChoice(question.id!, e.value ?? null)}
                    className={`w-full md:w-20rem ${isMissing ? "p-invalid" : ""}`}
                  />
                )}

                {question.question_type === "multiple_choice" && (
                  <div className={`flex flex-column gap-2 p-2 border-round ${isMissing ? "border-1 border-red-300 surface-50" : ""}`}>
                    {question.choices?.map((choice) => (
                      <div key={choice.id} className="flex align-items-center gap-2">
                        <Checkbox
                          inputId={`q${question.id}_c${choice.id}`}
                          value={choice.id}
                          checked={currentAnswer?.selected_choices?.includes(choice.id!) ?? false}
                          onChange={(e) => updateMultiChoice(question.id!, choice.id!, e.checked!)}
                        />
                        <label htmlFor={`q${question.id}_c${choice.id}`} className="cursor-pointer text-900">
                          {choice.text}
                        </label>
                      </div>
                    ))}
                  </div>
                )}

                {question.question_type === "rating" && (
                  <div className={`flex flex-column align-items-start gap-1 p-2 border-round ${isMissing ? "border-1 border-red-300 surface-50" : ""}`}>
                    <Rating
                      value={currentAnswer?.rating_answer ?? 0}
                      onChange={(e) => updateRating(question.id!, e.value!)}
                      cancel={false}
                      stars={5}
                    />
                    <span className={isMissing ? "text-red-500 text-xs font-semibold" : "text-500 text-xs"}>
                      {currentAnswer?.rating_answer
                        ? `${currentAnswer.rating_answer} / 5 seçildi`
                        : "1'den 5'e kadar değerlendirin"}
                    </span>
                  </div>
                )}
              </Card>
            </div>
          );
        })}
      </div>

      <div className="flex justify-content-between mt-4">
        <Button
          label="Önceki Bölüm"
          icon="pi pi-arrow-left"
          outlined
          disabled={currentPathIndex <= 0}
          onClick={handlePrevSection}
        />
        {currentPathIndex >= 0 && currentPathIndex < sectionPath.length - 1 ? (
          <Button
            label="Sonraki Bölüm"
            icon="pi pi-arrow-right"
            iconPos="right"
            size="large"
            onClick={handleNextSection}
          />
        ) : (
          <Button
            label="Anketi Gönder"
            icon="pi pi-send"
            size="large"
            onClick={() => {
              if (validateCurrentSection()) setShowConfirm(true);
            }}
          />
        )}
      </div>

      <Dialog
        header="Anketi Gönder"
        visible={showConfirm}
        onHide={() => setShowConfirm(false)}
        style={{ width: "min(400px, 95vw)" }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button label="İptal" icon="pi pi-times" outlined severity="secondary" onClick={() => setShowConfirm(false)} disabled={submitting} />
            <Button label="Gönder" icon="pi pi-send" loading={submitting} onClick={handleSubmit} />
          </div>
        }
      >
        <div className="flex flex-column gap-2">
          <p className="m-0 text-900">
            <strong>{answeredCount}</strong> soruyu cevapladınız ({questions.length - answeredCount} soru boş bırakıldı).
          </p>
          <p className="m-0 text-500 text-sm">Cevaplarınızı gönderdikten sonra değiştiremezsiniz. Onaylıyor musunuz?</p>
        </div>
      </Dialog>
    </div>
  );
};

export default DynamikAnketDoldur;
